import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { agentApplicationAPI } from '../services/api';
import './CreateApplication.css';

const STEPS = [
  { label: 'Visa Selection', key: 'visa' },
  { label: 'Documents', key: 'documents' },
  { label: 'Identification & Submit', key: 'submit' }
];

const CreateApplication = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1: Visa
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [visaConfigs, setVisaConfigs] = useState([]);
  const [selectedVisa, setSelectedVisa] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [agentNotes, setAgentNotes] = useState('');
  const [requiredDocs, setRequiredDocs] = useState([]);

  // Step 2: Documents (OCR-only, files held in state)
  // { docTypeId: { status, extractedData, file, fileName } }
  const [docStates, setDocStates] = useState({});
  const fileInputRefs = useRef({});

  // Step 3: Identification + Submit
  const [userForm, setUserForm] = useState({ firstName: '', lastName: '', email: '', whatsappNumber: '' });
  const [foundUser, setFoundUser] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [userLookupDone, setUserLookupDone] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // Load countries on mount
  useEffect(() => {
    agentApplicationAPI.getCountries()
      .then(res => setCountries(res.data?.data?.countries || []))
      .catch(() => {});
  }, []);

  // Load visa configs when country changes
  useEffect(() => {
    if (!selectedCountry) { setVisaConfigs([]); setSelectedVisa(''); return; }
    agentApplicationAPI.getVisaConfigs(selectedCountry)
      .then(res => setVisaConfigs(res.data?.data?.configs || []))
      .catch(() => setVisaConfigs([]));
  }, [selectedCountry]);

  // When visa is selected, fetch required docs to show in step 2
  useEffect(() => {
    if (!selectedVisa) { setRequiredDocs([]); return; }
    // We get required docs from create endpoint, but we need them before creating.
    // Use getVisaConfigs response or a separate call. For now, required docs come after create.
    // We'll fetch them when moving to step 2 via a temporary create-like call.
  }, [selectedVisa]);

  // --- Step 1 → 2: Fetch required docs for selected visa ---
  const handleGoToDocuments = async () => {
    setError('');
    if (!selectedVisa) { setError('Please select a visa type'); return; }

    setLoading(true);
    try {
      // Create a temporary application just to get required docs + pricing
      // Actually, we need to fetch required docs without creating. Let's use a create call
      // that we'll store data from, but we won't have a userId yet.
      // Instead, fetch required docs from the visa-configs data or add a docs endpoint.
      // For now: we move to step 2 and the required docs will be fetched via a new endpoint.

      // Fetch required documents for this visa config
      const res = await agentApplicationAPI.getVisaConfigs(selectedCountry);
      const configs = res.data?.data?.configs || [];
      const config = configs.find(c => String(c.id) === String(selectedVisa));

      if (!config) { setError('Visa config not found'); setLoading(false); return; }

      // We need required docs — let's use a dedicated endpoint or get from create response later
      // For now, we'll get docs when we call create at step 3.
      // But we need docs at step 2 to know which ones to upload.
      // Solution: add a getRequiredDocs call or use the create response.
      // Quick approach: fetch from an endpoint we'll add. For now, move to step 2 and
      // get required docs from a separate API call.

      const docsRes = await agentApplicationAPI.getRequiredDocs(selectedVisa);
      const docs = docsRes.data?.data?.documents || [];
      setRequiredDocs(docs);

      // Init doc states
      const states = {};
      docs.forEach(d => { states[d.documentTypeId] = { status: 'pending', extractedData: null, file: null, fileName: '' }; });
      setDocStates(states);

      setCurrentStep(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2: Extract document (OCR only, no storage) ---
  const handleFileSelect = async (documentTypeId, file) => {
    if (!file) return;
    setError('');

    setDocStates(prev => ({
      ...prev,
      [documentTypeId]: { ...prev[documentTypeId], status: 'extracting', file, fileName: file.name }
    }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentTypeId', documentTypeId);

      const res = await agentApplicationAPI.extractDocument(formData);
      const data = res.data;

      if (data.success) {
        setDocStates(prev => ({
          ...prev,
          [documentTypeId]: {
            status: 'extracted',
            extractedData: data.data?.extractedData || null,
            file,
            fileName: file.name
          }
        }));
      } else {
        setDocStates(prev => ({
          ...prev,
          [documentTypeId]: { ...prev[documentTypeId], status: 'error', file: null }
        }));
        setError(data.message || 'Extraction failed');
      }
    } catch (err) {
      setDocStates(prev => ({
        ...prev,
        [documentTypeId]: { ...prev[documentTypeId], status: 'error', file: null }
      }));
      setError(err.response?.data?.message || 'Extraction failed');
    }
  };

  const handleExtractedDataChange = (documentTypeId, field, value) => {
    setDocStates(prev => ({
      ...prev,
      [documentTypeId]: {
        ...prev[documentTypeId],
        extractedData: { ...prev[documentTypeId].extractedData, [field]: value }
      }
    }));
  };

  const handleConfirmExtraction = (documentTypeId) => {
    setDocStates(prev => ({
      ...prev,
      [documentTypeId]: { ...prev[documentTypeId], status: 'confirmed' }
    }));
  };

  // --- Step 3: Auto-fill name from passport OCR ---
  useEffect(() => {
    if (currentStep !== 2) return;
    // Try to auto-fill name from passport extraction
    const passportDocTypes = [1, 33, 34, 35]; // passport type IDs
    for (const docTypeId of passportDocTypes) {
      const state = docStates[docTypeId];
      if (state && state.extractedData && (state.status === 'extracted' || state.status === 'confirmed')) {
        const data = state.extractedData;
        if (data.firstName && !userForm.firstName) {
          setUserForm(prev => ({
            ...prev,
            firstName: data.firstName || prev.firstName,
            lastName: data.lastName || prev.lastName
          }));
        }
        break;
      }
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Step 3: Find/Create User ---
  const handleFindUser = async () => {
    setError('');
    if (!userForm.firstName.trim()) { setError('First name is required'); return; }
    if (!userForm.email.trim() && !userForm.whatsappNumber.trim()) {
      setError('Email or WhatsApp number is required');
      return;
    }

    setLoading(true);
    try {
      const res = await agentApplicationAPI.findUser({
        firstName: userForm.firstName.trim(),
        lastName: userForm.lastName.trim(),
        email: userForm.email.trim() || undefined,
        whatsappNumber: userForm.whatsappNumber.trim() || undefined
      });
      const data = res.data;
      if (data.success) {
        setFoundUser(data.data.user);
        setIsNewUser(data.data.isNew);
        setUserLookupDone(true);
      } else {
        setError(data.message || 'Failed to find user');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to find user');
    } finally {
      setLoading(false);
    }
  };

  const resetUserLookup = () => {
    setFoundUser(null);
    setIsNewUser(false);
    setUserLookupDone(false);
  };

  // --- Step 3: Submit (create app + upload docs + submit) ---
  const handleSubmit = async () => {
    if (!userLookupDone || !foundUser) {
      setError('Please identify the user first');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      // 1. Create application
      const createRes = await agentApplicationAPI.create({
        userId: foundUser.id,
        visaConfigId: parseInt(selectedVisa),
        travelDate: travelDate || undefined,
        notes: agentNotes.trim() || undefined
      });

      if (!createRes.data.success) {
        setError(createRes.data.message || 'Failed to create application');
        setSubmitting(false);
        return;
      }

      const appId = createRes.data.data.application.id;
      const appData = createRes.data.data;

      // 2. Upload all documents that have files
      const docsToUpload = Object.entries(docStates).filter(
        ([, state]) => state.file && ['extracted', 'confirmed'].includes(state.status)
      );

      for (const [docTypeId, state] of docsToUpload) {
        try {
          const formData = new FormData();
          formData.append('file', state.file);
          formData.append('documentTypeId', docTypeId);

          const uploadRes = await agentApplicationAPI.uploadDocument(appId, formData);

          // If extracted data was confirmed/edited, update it
          if (state.status === 'confirmed' && state.extractedData && uploadRes.data?.data?.userDocumentId) {
            await agentApplicationAPI.confirmDocument(appId, {
              userDocumentId: uploadRes.data.data.userDocumentId,
              extractedData: state.extractedData
            });
          }
        } catch (uploadErr) {
          console.error(`Failed to upload doc type ${docTypeId}:`, uploadErr);
          // Continue with other docs
        }
      }

      // 3. Submit application
      const submitRes = await agentApplicationAPI.submit(appId, {
        paymentStatus,
        notifyUser: notifyWhatsapp || notifyEmail
      });

      if (submitRes.data.success) {
        setSubmitted(true);
        setSubmitResult({
          ...submitRes.data.data,
          pricing: appData.pricing
        });
      } else {
        setError(submitRes.data.message || 'Failed to submit');
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.data?.missingDocuments) {
        setError(`Missing mandatory documents: ${errData.data.missingDocuments.join(', ')}`);
      } else {
        setError(errData?.message || 'Failed to submit application');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 0: return !!selectedVisa;
      case 1: return true;
      case 2: return !submitted;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      handleGoToDocuments();
      return;
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      setError('');
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      setCurrentStep(0);
      setError('');
    }
    // Don't allow back from step 3 to step 2 (docs are already in state, it's fine to go back)
    if (currentStep === 2) {
      setCurrentStep(1);
      setError('');
    }
  };

  // Get selected visa config details
  const selectedVisaConfig = visaConfigs.find(v => String(v.id) === String(selectedVisa));

  // Count docs
  const extractedCount = Object.values(docStates).filter(d => ['extracted', 'confirmed'].includes(d.status)).length;
  const mandatoryCount = requiredDocs.filter(d => d.isMandatory).length;
  const mandatoryDone = requiredDocs.filter(d => d.isMandatory && ['extracted', 'confirmed'].includes(docStates[d.documentTypeId]?.status)).length;

  return (
    <div className="create-app-layout">
      <Sidebar />
      <main className="create-app-main">
        {/* Header */}
        <div className="create-app-header">
          <h1>CREATE APPLICATION</h1>
          <button className="btn-back" onClick={() => navigate('/command-center')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            BACK
          </button>
        </div>

        {/* Steps */}
        <div className="steps-indicator">
          {STEPS.map((step, idx) => (
            <div
              key={step.key}
              className={`step-item ${idx === currentStep && !submitted ? 'active' : ''} ${idx < currentStep || (idx === currentStep && submitted) ? 'completed' : ''}`}
            >
              <span className="step-number">
                {idx < currentStep || (idx === currentStep && submitted) ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  idx + 1
                )}
              </span>
              {step.label}
            </div>
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Step Content */}
        <div className="wizard-card">
          {/* STEP 1: Visa Selection */}
          {currentStep === 0 && (
            <>
              <h2>Visa Selection</h2>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Destination Country *</label>
                  <select
                    className="form-select"
                    value={selectedCountry}
                    onChange={e => { setSelectedCountry(e.target.value); setSelectedVisa(''); }}
                  >
                    <option value="">Select country</option>
                    {countries.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Visa Type *</label>
                  <select
                    className="form-select"
                    value={selectedVisa}
                    onChange={e => setSelectedVisa(e.target.value)}
                    disabled={!selectedCountry}
                  >
                    <option value="">Select visa type</option>
                    {visaConfigs.map(vc => (
                      <option key={vc.id} value={vc.id}>
                        {vc.visa_name} — {vc.visa_type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Travel Date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={travelDate}
                    onChange={e => setTravelDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Agent Notes</label>
                  <input
                    className="form-input"
                    value={agentNotes}
                    onChange={e => setAgentNotes(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
              </div>

              {selectedVisaConfig && (
                <div className="visa-details-card">
                  <h3>{selectedVisaConfig.visa_name}</h3>
                  <div className="visa-detail-row">
                    <span>Type</span>
                    <span>{selectedVisaConfig.visa_type}</span>
                  </div>
                  <div className="visa-detail-row">
                    <span>Stay Duration</span>
                    <span>{selectedVisaConfig.stay_duration || '\u2014'}</span>
                  </div>
                  <div className="visa-detail-row">
                    <span>Entries</span>
                    <span>{selectedVisaConfig.entries_allowed || '\u2014'}</span>
                  </div>
                  <div className="visa-detail-row">
                    <span>Processing Time</span>
                    <span>{selectedVisaConfig.processing_time_days ? `${selectedVisaConfig.processing_time_days} days` : '\u2014'}</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* STEP 2: Documents (OCR extraction, no storage yet) */}
          {currentStep === 1 && (
            <>
              <h2>Upload Documents ({extractedCount}/{requiredDocs.length})</h2>
              <div className="doc-upload-list">
                {requiredDocs.map(doc => {
                  const state = docStates[doc.documentTypeId] || { status: 'pending' };
                  return (
                    <div
                      key={doc.documentTypeId}
                      className={`doc-upload-card ${['extracted', 'confirmed'].includes(state.status) ? 'uploaded' : ''}`}
                    >
                      <div className="doc-upload-header">
                        <h4>{doc.name}</h4>
                        {state.status === 'confirmed' ? (
                          <span className="doc-badge-uploaded">CONFIRMED</span>
                        ) : state.status === 'extracted' ? (
                          <span className="doc-badge-uploaded">EXTRACTED</span>
                        ) : doc.isMandatory ? (
                          <span className="doc-badge-mandatory">MANDATORY</span>
                        ) : (
                          <span className="doc-badge-optional">OPTIONAL</span>
                        )}
                      </div>

                      {(state.status === 'pending' || state.status === 'error') && (
                        <div
                          className="doc-upload-zone"
                          onClick={() => fileInputRefs.current[doc.documentTypeId]?.click()}
                        >
                          <input
                            type="file"
                            ref={el => fileInputRefs.current[doc.documentTypeId] = el}
                            accept="image/jpeg,image/png,image/webp,application/pdf"
                            onChange={e => {
                              if (e.target.files[0]) handleFileSelect(doc.documentTypeId, e.target.files[0]);
                            }}
                          />
                          <span>
                            {state.status === 'error' ? 'Extraction failed \u2014 click to retry' : 'Click to upload (JPEG, PNG, WebP, PDF)'}
                          </span>
                        </div>
                      )}

                      {state.status === 'extracting' && (
                        <div className="doc-uploading">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                            <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          Extracting data from {state.fileName}...
                        </div>
                      )}

                      {/* Extracted data preview */}
                      {state.status === 'extracted' && state.extractedData && (
                        <div className="extracted-data">
                          <h5>Extracted Data from {state.fileName}</h5>
                          <div className="extracted-fields">
                            {Object.entries(state.extractedData)
                              .filter(([key]) => !key.startsWith('_'))
                              .map(([key, value]) => (
                                <div key={key} className="extracted-field">
                                  <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</label>
                                  <input
                                    value={value || ''}
                                    onChange={e => handleExtractedDataChange(doc.documentTypeId, key, e.target.value)}
                                  />
                                </div>
                              ))}
                          </div>
                          <div className="doc-actions">
                            <button
                              className="btn-sm btn-confirm"
                              onClick={() => handleConfirmExtraction(doc.documentTypeId)}
                            >
                              Confirm Data
                            </button>
                          </div>
                        </div>
                      )}

                      {/* No extraction needed (e.g. photos) */}
                      {state.status === 'extracted' && !state.extractedData && (
                        <div style={{ fontSize: 12, color: 'var(--color-success)', marginTop: 8 }}>
                          File selected: {state.fileName}
                        </div>
                      )}

                      {state.status === 'confirmed' && (
                        <div style={{ fontSize: 12, color: 'var(--color-success)', marginTop: 8 }}>
                          Data confirmed \u2014 {state.fileName}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {requiredDocs.length === 0 && (
                <div className="wizard-loading">No documents required for this visa type</div>
              )}
            </>
          )}

          {/* STEP 3: Identification & Submit */}
          {currentStep === 2 && !submitted && (
            <>
              <h2>Identification & Submit</h2>

              {/* User identification form */}
              <div className="review-section">
                <h3>Applicant Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      className="form-input"
                      value={userForm.firstName}
                      onChange={e => { setUserForm({ ...userForm, firstName: e.target.value }); resetUserLookup(); }}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input
                      className="form-input"
                      value={userForm.lastName}
                      onChange={e => { setUserForm({ ...userForm, lastName: e.target.value }); resetUserLookup(); }}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      className="form-input"
                      type="email"
                      value={userForm.email}
                      onChange={e => { setUserForm({ ...userForm, email: e.target.value }); resetUserLookup(); }}
                      placeholder="user@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">WhatsApp Number</label>
                    <input
                      className="form-input"
                      value={userForm.whatsappNumber}
                      onChange={e => { setUserForm({ ...userForm, whatsappNumber: e.target.value }); resetUserLookup(); }}
                      placeholder="+91XXXXXXXXXX"
                    />
                    <span className="form-hint">Include country code</span>
                  </div>
                </div>

                {!userLookupDone && (
                  <div style={{ marginTop: 12 }}>
                    <button className="btn-primary" onClick={handleFindUser} disabled={loading}>
                      {loading ? 'Searching...' : 'Find / Create User'}
                      {!loading && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                      )}
                    </button>
                  </div>
                )}

                {userLookupDone && foundUser && (
                  <div className="user-found-card">
                    <h3>
                      {foundUser.firstName} {foundUser.lastName}
                      {isNewUser && <span className="user-new-badge">NEW USER</span>}
                    </h3>
                    <p>
                      {foundUser.email && <>Email: {foundUser.email}<br /></>}
                      {foundUser.whatsappNumber && <>WhatsApp: {foundUser.whatsappNumber}<br /></>}
                      User ID: {foundUser.id}
                    </p>
                    {foundUser.vaultDocuments && foundUser.vaultDocuments.length > 0 && (
                      <p style={{ marginTop: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
                        {foundUser.vaultDocuments.length} document(s) already in vault
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Visa summary */}
              {selectedVisaConfig && (
                <div className="review-section">
                  <h3>Visa</h3>
                  <div className="review-grid">
                    <div className="review-item">
                      <span>Visa</span>
                      <span>{selectedVisaConfig.visa_name}</span>
                    </div>
                    <div className="review-item">
                      <span>Country</span>
                      <span>{selectedVisaConfig.country_name}</span>
                    </div>
                    <div className="review-item">
                      <span>Type</span>
                      <span>{selectedVisaConfig.visa_type}</span>
                    </div>
                    {travelDate && (
                      <div className="review-item">
                        <span>Travel Date</span>
                        <span>{travelDate}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents summary */}
              <div className="review-section">
                <h3>Documents ({mandatoryDone}/{mandatoryCount} mandatory)</h3>
                <ul className="review-docs">
                  {requiredDocs.map(doc => {
                    const state = docStates[doc.documentTypeId];
                    const isDone = ['extracted', 'confirmed'].includes(state?.status);
                    return (
                      <li key={doc.documentTypeId}>
                        <span className="check-icon">{isDone ? '\u2713' : '\u2717'}</span>
                        {doc.name}
                        {doc.isMandatory && !isDone && <span style={{ color: 'var(--color-error)', marginLeft: 8, fontSize: 10 }}>MISSING</span>}
                        {state?.status === 'confirmed' && <span style={{ color: 'var(--color-success)', marginLeft: 8, fontSize: 10 }}>CONFIRMED</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Payment status */}
              <div className="review-section">
                <h3>Payment Status</h3>
                <select
                  className="form-select"
                  value={paymentStatus}
                  onChange={e => setPaymentStatus(e.target.value)}
                  style={{ maxWidth: 300 }}
                >
                  <option value="pending">Pending</option>
                  <option value="paid_offline">Paid Offline</option>
                  <option value="waived">Waived</option>
                </select>
              </div>

              {/* Notifications */}
              {userLookupDone && foundUser && (
                <div className="review-section">
                  <h3>Notify User</h3>
                  <div className="notify-options">
                    {foundUser.whatsappNumber && (
                      <label className="notify-option">
                        <input type="checkbox" checked={notifyWhatsapp} onChange={e => setNotifyWhatsapp(e.target.checked)} />
                        WhatsApp ({foundUser.whatsappNumber})
                      </label>
                    )}
                    {foundUser.email && (
                      <label className="notify-option">
                        <input type="checkbox" checked={notifyEmail} onChange={e => setNotifyEmail(e.target.checked)} />
                        Email ({foundUser.email})
                      </label>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Submitted success */}
          {currentStep === 2 && submitted && submitResult && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h2 style={{ marginTop: 16, fontSize: 18, color: 'var(--color-text-primary)' }}>Application Submitted</h2>
              <p style={{ color: 'var(--color-text-secondary)', marginTop: 8 }}>
                Application <strong>{submitResult.applicationNumber}</strong> has been submitted successfully.
              </p>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 12, marginTop: 4 }}>
                Payment: {submitResult.paymentStatus} | Notification: {submitResult.notificationSent ? 'Sent' : 'Not sent'}
              </p>
              <button
                className="btn-primary"
                style={{ margin: '24px auto 0' }}
                onClick={() => navigate('/command-center')}
              >
                Back to Command Center
              </button>
            </div>
          )}

          {/* Navigation buttons */}
          {!(currentStep === 2 && submitted) && (
            <div className="wizard-actions">
              <button
                className="btn-secondary"
                onClick={handleBack}
                style={{ visibility: currentStep === 0 ? 'hidden' : 'visible' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Back
              </button>

              {currentStep < 2 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={!canGoNext() || loading}
                >
                  {loading ? 'Loading...' : 'Next'}
                  {!loading && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  )}
                </button>
              ) : (
                <button
                  className="btn-success"
                  onClick={handleSubmit}
                  disabled={submitting || !userLookupDone}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CreateApplication;
